import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  getQueryHistory,
  getQuery,
  deleteQuery,
  rerunQuery,
  getQueryHistoryStats
} from '../services/api';

// Map query types to their respective page routes
const QUERY_TYPE_ROUTES = {
  classification: '/classify',
  sanitization: '/tearline',
  foia: '/foia',
  foia_discovery: '/foia-discovery',
  hyde: '/classify',  // HyDE queries show in classify
  summarization: '/classify',
  extraction: '/classify',
  legal_discovery: '/legal'
};

// Get human-readable page name for query type
const getPageName = (queryType) => {
  const names = {
    classification: 'Classify',
    sanitization: 'Tearline',
    foia: 'FOIA',
    foia_discovery: 'FOIA Discovery',
    hyde: 'Classify',
    summarization: 'Classify',
    extraction: 'Classify',
    legal_discovery: 'Legal Discovery'
  };
  return names[queryType] || 'Results';
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    pending: 'bg-yellow-100 text-yellow-800',
    running: 'bg-blue-100 text-blue-800 animate-pulse',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800'
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Query type badge component
const QueryTypeBadge = ({ type }) => {
  const typeStyles = {
    classification: 'bg-purple-100 text-purple-800',
    sanitization: 'bg-indigo-100 text-indigo-800',
    foia: 'bg-teal-100 text-teal-800',
    foia_discovery: 'bg-emerald-100 text-emerald-800',
    hyde: 'bg-cyan-100 text-cyan-800',
    summarization: 'bg-orange-100 text-orange-800',
    extraction: 'bg-pink-100 text-pink-800',
    legal_discovery: 'bg-amber-100 text-amber-800'
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeStyles[type] || 'bg-gray-100 text-gray-800'}`}>
      {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
};

// Query detail modal
const QueryDetailModal = ({ query, onClose, onRerun, onViewResults }) => {
  if (!query) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const canViewResults = query.status === 'completed' && query.result && QUERY_TYPE_ROUTES[query.query_type];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold">Query Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <span className="text-sm text-gray-500">Status</span>
              <div className="mt-1"><StatusBadge status={query.status} /></div>
            </div>
            <div>
              <span className="text-sm text-gray-500">Type</span>
              <div className="mt-1"><QueryTypeBadge type={query.query_type} /></div>
            </div>
            <div>
              <span className="text-sm text-gray-500">Created</span>
              <div className="mt-1 text-sm">{formatDate(query.created_at)}</div>
            </div>
            <div>
              <span className="text-sm text-gray-500">Completed</span>
              <div className="mt-1 text-sm">{formatDate(query.completed_at)}</div>
            </div>
          </div>

          {query.model_used && (
            <div className="mb-4">
              <span className="text-sm text-gray-500">Model Used</span>
              <div className="mt-1 text-sm font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                {query.model_used}
              </div>
            </div>
          )}

          {query.execution_time_ms && (
            <div className="mb-4">
              <span className="text-sm text-gray-500">Execution Time</span>
              <div className="mt-1 text-sm">{(query.execution_time_ms / 1000).toFixed(2)}s</div>
            </div>
          )}

          {/* Input Text */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Input Text</h3>
            <div className="bg-gray-50 p-3 rounded border text-sm max-h-48 overflow-y-auto whitespace-pre-wrap">
              {query.input_text}
            </div>
          </div>

          {/* Input Parameters */}
          {query.input_params && Object.keys(query.input_params).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Input Parameters</h3>
              <div className="bg-gray-50 p-3 rounded border text-sm">
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(query.input_params, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Result */}
          {query.result && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Result</h3>
              <div className="bg-green-50 p-3 rounded border border-green-200 text-sm max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(query.result, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Error */}
          {query.error && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-red-700 mb-2">Error</h3>
              <div className="bg-red-50 p-3 rounded border border-red-200 text-sm text-red-800">
                {query.error}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          {canViewResults && (
            <button
              onClick={() => onViewResults(query)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View in {getPageName(query.query_type)}
            </button>
          )}
          {query.status === 'completed' && (
            <button
              onClick={() => onRerun(query.id)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Rerun Query
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main QueryHistory component
const QueryHistory = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [filters, setFilters] = useState({
    query_type: '',
    status: '',
    limit: 20,
    offset: 0
  });

  // Get user_id for filtering - use username from auth context
  const userId = user?.username || 'anonymous';

  // Fetch query history filtered by current user
  const { data: historyData, isLoading, refetch } = useQuery({
    queryKey: ['queryHistory', filters, userId],
    queryFn: () => getQueryHistory({ ...filters, user_id: userId }),
    refetchInterval: (data) => {
      // Auto-refresh if there are running queries
      const hasRunning = data?.queries?.some(q => q.status === 'running' || q.status === 'pending');
      return hasRunning ? 2000 : false;
    }
  });

  // Fetch stats for current user
  const { data: statsData } = useQuery({
    queryKey: ['queryHistoryStats', userId],
    queryFn: () => getQueryHistoryStats(userId)
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteQuery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queryHistory'] });
      queryClient.invalidateQueries({ queryKey: ['queryHistoryStats'] });
    }
  });

  // Rerun mutation
  const rerunMutation = useMutation({
    mutationFn: rerunQuery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queryHistory'] });
      setSelectedQuery(null);
    }
  });

  // Fetch full query details when selected
  const fetchQueryDetails = useCallback(async (queryId) => {
    try {
      const query = await getQuery(queryId);
      setSelectedQuery(query);
    } catch (error) {
      console.error('Failed to fetch query details:', error);
    }
  }, []);

  const handleDelete = (queryId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this query?')) {
      deleteMutation.mutate(queryId);
    }
  };

  const handleRerun = (queryId) => {
    rerunMutation.mutate(queryId);
  };

  // Navigate to the relevant page with query results pre-populated
  const handleViewResults = useCallback((query) => {
    const route = QUERY_TYPE_ROUTES[query.query_type];
    if (!route) {
      console.warn('No route mapping for query type:', query.query_type);
      return;
    }

    // Close the modal
    setSelectedQuery(null);

    // Navigate to the page with the restored query state
    navigate(route, {
      state: {
        restoredQuery: {
          id: query.id,
          query_type: query.query_type,
          input_text: query.input_text,
          input_params: query.input_params || {},
          result: query.result,
          created_at: query.created_at,
          completed_at: query.completed_at,
          execution_time_ms: query.execution_time_ms,
          model_used: query.model_used
        }
      }
    });
  }, [navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;

    // Less than 1 minute
    if (diff < 60000) return 'Just now';
    // Less than 1 hour
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    // Less than 24 hours
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    // Otherwise show date
    return date.toLocaleDateString();
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const queryTypes = ['classification', 'sanitization', 'foia', 'foia_discovery', 'hyde', 'summarization', 'extraction', 'legal_discovery'];
  const statuses = ['pending', 'running', 'completed', 'failed'];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">{statsData.total_queries || 0}</div>
            <div className="text-sm text-gray-500">Total Queries</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-green-600">{statsData.by_status?.completed || 0}</div>
            <div className="text-sm text-gray-500">Completed</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-blue-600">{statsData.by_status?.running || 0}</div>
            <div className="text-sm text-gray-500">Running</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-yellow-600">{statsData.by_status?.pending || 0}</div>
            <div className="text-sm text-gray-500">Pending</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-red-600">{statsData.by_status?.failed || 0}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Query Type</label>
            <select
              value={filters.query_type}
              onChange={(e) => setFilters({ ...filters, query_type: e.target.value, offset: 0 })}
              className="border rounded px-3 py-1.5 text-sm"
            >
              <option value="">All Types</option>
              {queryTypes.map(type => (
                <option key={type} value={type}>
                  {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, offset: 0 })}
              className="border rounded px-3 py-1.5 text-sm"
            >
              <option value="">All Statuses</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Show</label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value), offset: 0 })}
              className="border rounded px-3 py-1.5 text-sm"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className="ml-auto">
            <button
              onClick={() => refetch()}
              className="px-4 py-1.5 bg-gray-100 rounded hover:bg-gray-200 text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Query Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Query
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Model
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    Loading...
                  </div>
                </td>
              </tr>
            ) : historyData?.queries?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No queries found
                </td>
              </tr>
            ) : (
              historyData?.queries?.map((query) => (
                <tr
                  key={query.id}
                  onClick={() => fetchQueryDetails(query.id)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 max-w-xs truncate" title={query.input_text}>
                      {truncateText(query.input_text, 60)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <QueryTypeBadge type={query.query_type} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={query.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-600">
                      {query.model_used ? truncateText(query.model_used, 20) : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(query.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {query.execution_time_ms ? `${(query.execution_time_ms / 1000).toFixed(1)}s` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => handleDelete(query.id, e)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {historyData && historyData.total > filters.limit && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {filters.offset + 1} to {Math.min(filters.offset + filters.limit, historyData.total)} of {historyData.total}
            </div>
            <div className="flex gap-2">
              <button
                disabled={filters.offset === 0}
                onClick={() => setFilters({ ...filters, offset: Math.max(0, filters.offset - filters.limit) })}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                disabled={filters.offset + filters.limit >= historyData.total}
                onClick={() => setFilters({ ...filters, offset: filters.offset + filters.limit })}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedQuery && (
        <QueryDetailModal
          query={selectedQuery}
          onClose={() => setSelectedQuery(null)}
          onRerun={handleRerun}
          onViewResults={handleViewResults}
        />
      )}
    </div>
  );
};

export default QueryHistory;
